import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useColorMode,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  getWidgetConfigs,
  UsedOnBoard,
  WidgetCategory,
  WidgetConfig,
  widgetBoardMap,
} from '../../widgets';
import { useRef, useState } from 'react';
import { Widget } from '../../useWidgetLayout';
import { WidgetOptions, WidgetType } from '../types';
import { convertToEnum, getEnumValues } from '@texas/utils/helpers/enumHelpers';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { widgetCategoryFriendlyName } from '../translations';
import { useActiveSession } from '@texas/hooks/useSession';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import {
  GenericWidgetSettings,
  GenericWidgetSettingsHandle,
} from './GenericWidgetSettings';
import {
  SpacerWidgetSettings,
  SpacerWidgetSettingsHandle,
} from './SpacerWidgetSettings';

export function AddWidget({
  onAdd,
  currentWidgets,
  board,
}: {
  onAdd: (
    type: WidgetType,
    name: string,
    color: string | null,
    options?: WidgetOptions,
  ) => void;
  currentWidgets: Widget[];
  board?: UsedOnBoard;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const session = useActiveSession();
  const configs = getWidgetConfigs(t);
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);

  const genericRef = useRef<GenericWidgetSettingsHandle>(null);
  const spacerRef = useRef<SpacerWidgetSettingsHandle>(null);

  function closeAndReset() {
    setSelectedType(null);
    onClose();
  }

  function addWidget() {
    if (!selectedType) return;
    const widget = configs[selectedType];
    const isSpacer = selectedType === WidgetType.Spacer;

    if (isSpacer) {
      const values = spacerRef.current?.getValues();
      if (!values) return;
      onAdd(
        widget.type,
        values.customWidgetTitle ? values.customWidgetTitle : widget.label,
        null,
        values,
      );
    } else {
      const values = genericRef.current?.getValues();
      if (!values) return;
      onAdd(
        widget.type,
        values.name.length === 0 ? widget.label : values.name,
        values.color,
      );
    }

    setSelectedType(null);
    onClose();
  }

  const configsArr = Object.values(configs).filter((c) => {
    const boards = widgetBoardMap[c.type];
    return !board || boards.length === 0 || boards.includes(board);
  });
  const selectedConfig = selectedType ? configs[selectedType] : null;
  const isSpacer = selectedType === WidgetType.Spacer;
  const showCustomization = !!selectedConfig;

  return (
    <>
      <Button variant="entityDirectorySecondary" onClick={onOpen}>
        {t('widgets.new')}
      </Button>

      <Drawer
        size="md"
        isOpen={isOpen}
        placement="left"
        onClose={closeAndReset}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{t('widgets.add')}</DrawerHeader>
          <DrawerBody>
            <Flex gap={4} direction="column">
              <Accordion allowMultiple={true} defaultIndex={[0]}>
                {getEnumValues(WidgetCategory).map((x) => {
                  const filteredWidgets = configsArr.filter(
                    (c) =>
                      c.category === x &&
                      !c.hidden &&
                      (c.isBeta === true
                        ? session.currentUser.betaFeatures
                        : true),
                  );

                  if (filteredWidgets.length === 0) return null;

                  return (
                    <AccordionItem key={x}>
                      <AccordionButton>
                        <Flex flex="1" alignItems="center" gap={2}>
                          <Heading fontSize="sm">
                            {widgetCategoryFriendlyName(
                              convertToEnum(WidgetCategory, x),
                              t,
                            )}
                          </Heading>
                          <Badge size="sm">
                            {t('widgets.count', {
                              count: filteredWidgets.length,
                            })}
                          </Badge>
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={2}>
                        <SimpleGrid columns={2} spacing={2}>
                          {filteredWidgets.map((c) => (
                            <WidgetTemplate
                              key={c.type}
                              current={
                                currentWidgets.filter((w) => w.type === c.type)
                                  .length
                              }
                              selected={selectedType === c.type}
                              config={c}
                              onSelect={() => setSelectedType(c.type)}
                            />
                          ))}
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              {showCustomization && (
                <Box
                  borderRadius="md"
                  border="1px solid"
                  borderColor="whiteAlpha.100"
                  bg="texas.bg.700"
                  p={4}
                  pos="sticky"
                  bottom={0}
                  animation={fadeInScaleAnimation()}
                  _light={{
                    borderColor: 'gray.100',
                    bg: 'white',
                  }}
                >
                  {isSpacer ? (
                    <SpacerWidgetSettings
                      key={selectedType}
                      ref={spacerRef}
                      namePlaceholder={selectedConfig.label}
                    />
                  ) : (
                    <GenericWidgetSettings
                      key={selectedType}
                      ref={genericRef}
                      initialName=""
                      initialColor={null}
                      namePlaceholder={selectedConfig.label}
                    />
                  )}
                </Box>
              )}
            </Flex>
          </DrawerBody>

          <DrawerFooter
            backgroundColor="gray.800"
            _light={{ backgroundColor: 'gray.50' }}
            gap={2}
          >
            <Button onClick={closeAndReset}>{t('general.close')}</Button>
            <Button
              variant="texas-solid"
              isDisabled={!selectedType}
              onClick={addWidget}
            >
              {t('general.add')}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function WidgetTemplate({
  config,
  selected,
  onSelect,
  current,
}: {
  config: WidgetConfig;
  selected: boolean;
  onSelect: () => void;
  current: number;
}) {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const maxReached = config.maximum !== null && current >= config.maximum;
  return (
    <FeatureGuard optOut={!config.isBeta}>
      <Button
        onClick={onSelect}
        isDisabled={maxReached}
        aria-pressed={selected}
        animation={fadeInScaleAnimation()}
        borderColor={selected ? (isDark ? 'white' : 'black') : 'transparent'}
        variant="widget"
        gap={2}
        padding={0}
        overflow="hidden"
      >
        <Flex p={4} flexDir="column" gap={2}>
          <Text>{config.label}</Text>
          <Text variant="sub" fontWeight="medium" fontSize="sm">
            {config.description}
          </Text>
          {maxReached && config.maximum && (
            <Badge w="fit-content">
              {t('widgets.common.maxCount', { count: config.maximum })}
            </Badge>
          )}
        </Flex>
      </Button>
    </FeatureGuard>
  );
}
